import GameLabel from '@/app/Components/GameLabel.jsx';
import { useCallback, useState, useRef, useEffect, Fragment, useMemo } from 'react';
import { Transition } from '@headlessui/react';
import { usePage } from '@inertiajs/react';
import styled from 'styled-components';
import classNames from 'classnames';
import Clarity from '@microsoft/clarity';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y } from 'swiper/modules';
import 'swiper/css';
import GTMEventManager from '@/app/GTMEventManager.js';
import { useTranslations } from '@/app/i18n.js';
import InfosIcon from '@/app/Components/InfosIcon.jsx';
import GameCardLastChance from '@/app/Components/GameCardLastChance.jsx';
import useIsMobile from '@/app/utils/isMobile.jsx';
import GameDetailsModal from './GameDetailsModal.jsx';

const GamesGrid = styled.div`
    @media (max-width: 768px) {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }
`;

const GamesGridItem = styled.div`
    transition: opacity 0.2s ease;
    @media screen and (min-width: 1024px) {
        &:hover {
            .image > img {
                transform: scale(1.08);
            }
            .logo {
                bottom: 0.75rem;
            }
        }
    }

    .image {
        min-height: 17vw;
        height: 17vw;
    }
    @media (min-width: 768px) and (max-width: 1024px) {
        .image {
            min-height: 30vw;
            height: 30vw;
        }
    }
    @media (min-width: 1024px) and (max-width: 1480px) {
        .image {
            min-height: 26vw;
            height: 26vw;
        }
    }
    @media (min-width: 1480px) and (max-width: 1536px) {
        .image {
            min-height: 22vw;
            height: 22vw;
        }
    }
    @media (max-width: 768px) {
        .image {
            min-height: 69vw;
            height: 69vw;
        }
    }
`;


const GameBadge = styled.div`
    top: 36px;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.06rem;
    line-height: 1;
    left: 0px;

    @media (min-width: 768px) {
        top: 40px;
        left: 0px;
        font-size: 0.7rem;
    }

    @media (min-width: 1024px) {
        font-size: 0.8rem;
    }

    & + .game-label {
        top: 0.7rem;
    }

    &.halloween {
        background: #ff7525 !important;
        color: black !important;
    }
`;

const GameLogo = styled.div`
    position: absolute;
    z-index: 50;
    bottom: 1.2rem;
    left: 50%;
    transform: translateX(-50%);
    min-width: 100px;

    width: 10rem;
    height: 6rem;
    max-width: 100%;
    max-height: 6rem;
    transition: all 0.32s ease-in-out;

    color: white;
    text-align: center;
    font-weight: 700;
    font-size: 1.25rem;
    white-space: nowrap;

    @media (min-width: 768px) {
        max-height: 6rem;
        bottom: 1.1rem;
        width: 12rem;
    }

    @media (min-width: 1024px) {
        bottom: 1.6rem;
        max-height: 7rem;
        width: 13rem;
    }

    @media (min-width: 1536px) {
        max-height: 8rem;
        height: 7rem;
        bottom: 1.4rem;
        width: 14rem;
    }

    @media (min-width: 1920px) {
        max-height: 10rem;
        bottom: 1.4rem;
        height: 7rem;
        width: 15rem;
    }

    img {
        object-fit: contain;
        object-position: center;
        height: 90%;
        width: 80%;
        margin: 0 auto;
    }

    &.large {
        @media screen and (max-width: 768px) {
            width: 16rem;
            height: 6rem;
            max-width: 100%;
            max-height: 6rem;
            bottom: 1.2rem !important;
            img {
                height: 100%;
            }
        }
    }

    &.xl {
        width: 19rem;
        height: 6rem;
        max-height: 6rem;
        bottom: 1.1rem !important;

        img {
            object-position: bottom;
        }

        &.inline {
            bottom: 2.6rem !important;
        }

        @media (min-width: 768px) {
            max-height: 6rem;
            bottom: 1.1rem !important;
            width: 17rem;
            &.inline {
                bottom: 2.4rem !important;
            }
        }

        @media (min-width: 1024px) {
            bottom: 1.6rem;
            max-height: 7rem;
            width: 18rem;
            &.inline {
                bottom: 2.4rem !important;
            }
        }

        @media (min-width: 1536px) {
            max-height: 8rem;
            height: 7rem;
            bottom: 1rem !important;
            width: 19rem;
            &.inline {
                bottom: 2.4rem !important;
            }
        }

        @media (min-width: 1920px) {
            max-height: 10rem;
            bottom: 1.2rem !important;
            height: 7rem;
            width: 23rem;
            &.inline {
                bottom: 2.4rem !important;
            }
        }
    }

    &.featured {
        @media screen and (max-width: 768px) {
            max-height: 8vh;
            img {
                object-position: bottom;
                max-width: 70%;
                margin: 0 auto;
            }
        }
    }
`;

const GamePoster = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 0.25rem;
    object-fit: cover;
    object-position: center;
    transition: transform 0.32s ease-in-out, filter 0.16s ease-out;
    // transform: scale(1.125);
    &:hover {
        transform: scale(1.08);
    }

    &.blurred {
        filter: blur(16px);
    }

    @media screen and (min-width: 1024px) {
        transition: transform 0.32s ease-in-out, filter 0.16s ease-out;
        &.blurred {
            filter: blur(24px);
        }
    }
`;

const FeaturedSlide = styled(SwiperSlide)`
    width: 18vw;
    height: 28vw;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    cursor: pointer;
    .container {
        overflow: hidden;
        border-radius: 0.6rem;
        border: 1px solid rgba(255, 255, 255, 0.1);
    }
    .main-poster {
        border-radius: 0.6rem;
        min-width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        transition: transform 0.32s ease-in-out, filter 0.16s ease-out;
    }
    &:hover {
        .main-poster {
            transform: scale(1.08);
        }
    }
    //@media (min-width: 768px) {
    //    height: 46vw;
    //}

    @media (max-width: 1366px) {
        width: 21vw;
        height: 32vw;
    }
    @media (max-width: 1024px) {
        width: 32vw;
        height: 46vw;
    }
    @media (max-width: 768px) {
        width: 42vw;
        height: 58vw;
    }
    //@media (min-width: 1536px) {
    //    height: 22vw;
    //}
`;

const GameSlide = styled(SwiperSlide)`
    height: auto;
    width: 76vw;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    cursor: pointer;
    border-radius: 0.1rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    @media (min-width: 768px) {
        width: 42vw;
    }
    @media (min-width: 1024px) {
        width: 32vw;
    }
    @media (min-width: 1366px) {
        width: 32vw;
    }
    @media (min-width: 1536px) {
        width: 28vw;
    }
    @media (min-width: 1920px) {
        width: 24vw;
    }
`;

const CategoriesSwiper = styled(Swiper)`
    &:after {
        content: '';
        position: absolute;
        top: 0;
        right: 0rem;
        height: 100%;
        width: 120px;
        background: linear-gradient(90deg, rgba(9,9,9,0) 0%, rgba(9,9,9,1) 100%);
        z-index: 1;
    }
    &.is-end:after {
        z-index: -1;
        opacity: 0;
    }
    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 120px;
        background: linear-gradient(-90deg, rgba(9,9,9,0) 0%, rgba(9,9,9,1) 100%);
        z-index: -1;
        opacity: 0;
    }
    &.is-scrolled:before {
        z-index: 2;
        opacity: 1;
    }
    @media (max-width: 1000px) {
        &:before, &:after {
            width: 20px;
        }
        &.is-scrolled:before {
            z-index: -1;
            opacity: 0;
        }
    }
    body:not(.dark) &:before, body:not(.dark) &:after {
        display: none !important;
    }
`;

const CatNavigationElement = styled.button`
    cursor: pointer;
    border-radius: 6rem;
    outline: none;
    background: rgba(10, 10, 10, 0.75);
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.42);
    height: 36px;
    width: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #56ddbb;
    &:disabled {
        opacity: 0;
        cursor: default;
    }

    @media screen and (max-width: 640px) {
        top: 50% !important;
    }
    @media (max-width: 768px) {
        height: 28px;
        width: 28px;
    }
`;

export default function ({ settings, games, onSelectGame, loading, loaded, categories }) {
    const page = usePage();
    const { isMobile } = useIsMobile();
    const __ = useTranslations();
    const locale = page.props.language;
    const { uniq_id: uniqId, session_id: sessionId } = page.props.auth;
    const [isScrolled, setIsScrolled] = useState(false);
    const [currentGame, setCurrentGame] = useState(null);

    // Top featured games swiper
    const topSwiperRef = useRef(null);

    // Categories swipers
    const catSwipers = useRef({});
    const catNextRef = useRef({});
    const catPrevRef = useRef({});

    const openGameModal = useCallback((game) => {
        setCurrentGame(game);
        GTMEventManager.ecommerce().viewItem(game);
    }, []);

    const closeGameModal = useCallback(() => {
        setCurrentGame(null);
    }, []);

    function updateNavButtons(swiper, categoryId, catPrevRef, catNextRef) {
        if (catPrevRef.current[categoryId]) {
            catPrevRef.current[categoryId].disabled = swiper.isBeginning;
        }
        if (catNextRef.current[categoryId]) {
            catNextRef.current[categoryId].disabled = swiper.isEnd;
        }
        if (swiper.isEnd) {
            catSwipers.current[categoryId].el.classList.add('is-end');
        } else {
            catSwipers.current[categoryId].el.classList.remove('is-end');
        }
        if (! swiper.isBeginning) {
            catSwipers.current[categoryId].el.classList.add('is-scrolled');
        } else {
            catSwipers.current[categoryId].el.classList.remove('is-scrolled');
        }
    }

    // Track page view
    useEffect(() => {
        if (!loading) {
            GTMEventManager.pageView('step_games');
            console.log(`[Clarity] session: ${uniqId} / ${sessionId} - step_games`);
            Clarity.identify(uniqId, sessionId, 'step_games');
        }
    }, [loading]);

    // Handle a scroll to top button
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 400);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const gameLabelContent = useCallback((game) => {
        const label = game.genre[locale];
        if (isMobile) {
            if (label === 'Expérience Culturelle') {
                return 'Cuturelle';
            }

            if (label === 'Cultural Experience') {
                return 'Cultural';
            }
        }

        return label;
    }, [isMobile]);

    return (
        <section className="relative h-full flex flex-col mb-10 games-grid">
            {!loading && (
                <>
                    <div className={classNames({
                        'px-4 sm:px-5 mb-4 pt-0 lg:pt-2 3xl:pt-6 overflow-hidden': true,
                    })}>
                        <Swiper
                            ref={topSwiperRef}
                            modules={[A11y]}
                            spaceBetween={12}
                            breakpoints={{
                                768: { spaceBetween: 24 }
                            }}
                            slidesPerView="auto"
                            className="w-full"
                            style={{ overflow: 'visible' }}
                        >
                            {games.filter((g) => g.popular === true).slice(0, 5).map((game, index) => (
                                <FeaturedSlide key={`featured-game-${index}`}>
                                    <div className="w-full h-full relative overflow-visible">
                                        <div className="w-full h-full overflow-hidden container relative rounded-[0.6rem]">
                                            <img className="main-poster" src={game.card} alt={game.name} onClick={() => onSelectGame(game)}/>
                                        </div>
                                        <GameCardLastChance
                                            lastChance={game.last_chance}
                                            variant="featured"
                                            xlLogo={game.slug === 'louvre-abu-dhabi' || game.slug === 'la-magie-opera' || game.slug === 'gladiator'}
                                        />
                                        <GameLogo className={`${(game.slug === 'louvre-abu-dhabi' || game.slug === 'la-magie-opera' || game.slug === 'gladiator') ? 'xl' : 'large'} featured`} onClick={() => onSelectGame(game)} style={{bottom: '0.25rem'}}>
                                            <img
                                                src={game.logo}
                                                srcSet={game.logoSrcset}
                                                alt={game.name}
                                                className="logo"
                                                style={{whiteSpace: 'nowrap'}}
                                                sizes="33vw"
                                                onLoad={(e) => {
                                                    if (e.target.dataset.sized === undefined) {
                                                        e.target.sizes = Math.ceil((e.target.getBoundingClientRect().width / window.innerWidth) * 100) + 'vw';
                                                        e.target.dataset.sized = ''
                                                    }
                                                }}
                                            />
                                        </GameLogo>
                                    </div>
                                    <div className="hidden sm:flex absolute top-0 right-0 p-3 gap-4 z-10" style={{transform: 'scale(0.9)'}}>
                                        <InfosIcon onClick={() => openGameModal(game)}/>
                                    </div>
                                    <div className="absolute bottom-0 -left-3 flex z-10">
                                        <span
                                            className="text-7xl 2xl:text-8xl font-bold text-white"
                                            style={{textShadow: '2px 0 #000, -2px 0 #000, 0 2px #000, 0 -2px #000, 1px 1px #000, -1px -1px #000, 1px -1px #000, -1px 1px #000'}}
                                        >
                                            {index + 1}
                                        </span>
                                    </div>
                                    <GameLabel className="game-label">{gameLabelContent(game)}</GameLabel>
                                    {game.new === true && game.last_chance === null && (
                                        <GameBadge className="game-badge z-10 absolute bg-primary py-1 px-2 sm:px-3 uppercase font-space">
                                            {__('book.common.new')}
                                        </GameBadge>
                                    )}
                                    {game.last_chance !== null && (
                                        <GameBadge className="game-badge z-10 absolute bg-warm-gradient py-1 px-2 sm:px-3 uppercase font-space">
                                            {__('book.common.last_chance')}
                                        </GameBadge>
                                    )}
                                    {/*{game.new === false && game.popular === true && (*/}
                                    {/*    <GameBadge className="absolute z-10 py-1 px-2 bg-c-orange sm:px-3 uppercase font-space">*/}
                                    {/*        {__('book.common.popular')}*/}
                                    {/*    </GameBadge>*/}
                                    {/*)}*/}
                                </FeaturedSlide>
                            ))}
                        </Swiper>
                    </div>
                    {categories.map((category) => (
                        <div className="px-4 sm:px-5 my-3 relative" key={category.id}>
                            <h2 className="dark:text-white font-semibold text-lg mb-2">{category.title_1[locale]} {category.title_2[locale]}</h2>
                            <CategoriesSwiper
                                modules={[Navigation, A11y]}
                                spaceBetween={16}
                                slidesPerView="auto"
                                className="w-full"
                                onSwiper={(swiper) => {
                                    catSwipers.current[category.id] = swiper;
                                    updateNavButtons(swiper, category.id, catPrevRef, catNextRef);
                                }}
                                onSlideChange={(swiper) => updateNavButtons(swiper, category.id, catPrevRef, catNextRef)}
                                onResize={(swiper) => updateNavButtons(swiper, category.id, catPrevRef, catNextRef)}
                                onUpdate={(swiper) => updateNavButtons(swiper, category.id, catPrevRef, catNextRef)}
                            >
                                {games.filter((g) => g.category_id === category.id).map((game, index) => (
                                    <GameSlide key={`game-${game.id}`}>
                                        <div className="flex flex-col relative cursor-pointer w-full h-full bg-very-dark-gray">
                                            <div className="relative z-10 h-full">
                                                <div className="h-full w-full image overflow-hidden relative rounded-sm">
                                                    <GamePoster
                                                        className="blurred"
                                                        src={game.main_poster}
                                                        srcSet={game.mainPosterSrcset}
                                                        alt={game.name}
                                                        sizes="1px"
                                                        onLoad={(e) => {
                                                            if (e.target.dataset.sized === undefined) {
                                                                e.target.sizes = Math.ceil(((e.target.getBoundingClientRect().width / window.innerWidth) * 100) * 1.5) + 'vw';
                                                                e.target.dataset.sized = '';
                                                                e.target.classList.remove('blurred');
                                                            }
                                                        }}
                                                        loading="lazy"
                                                    />
                                                </div>
                                                <GameCardLastChance
                                                    lastChance={game.last_chance}
                                                    xlLogo={game.slug === 'louvre-abu-dhabi' || game.slug === 'la-magie-opera' || game.slug === 'gladiator'}
                                                />
                                                <GameLogo onClick={() => onSelectGame(game)} className={`inline ${(game.slug === 'louvre-abu-dhabi' || game.slug === 'la-magie-opera' || game.slug === 'gladiator') ? 'xl' : ''}`}>
                                                    <img
                                                        src={game.logo}
                                                        srcSet={game.logoSrcset}
                                                        alt={game.name}
                                                        className={`logo`}
                                                        style={{whiteSpace: 'nowrap'}}
                                                        sizes="33vw"
                                                        onLoad={(e) => {
                                                            if (e.target.dataset.sized === undefined) {
                                                                e.target.sizes = Math.ceil((e.target.getBoundingClientRect().width / window.innerWidth) * 100) + 'vw';
                                                                e.target.dataset.sized = ''
                                                            }
                                                        }}
                                                    />
                                                </GameLogo>
                                                <div
                                                    onClick={() => onSelectGame(game)}
                                                    style={{
                                                        position: 'absolute',
                                                        width: '100%',
                                                        height: '100%',
                                                        top: '0',
                                                        left: '0',
                                                        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 42%, #090909 100%)',
                                                    }}
                                                />
                                                <div className="absolute top-0 right-0 p-3 flex gap-4 z-10 scale-90">
                                                    <InfosIcon onClick={() => openGameModal(game)}/>
                                                </div>
                                            </div>
                                            <div
                                                onClick={() => onSelectGame(game)}
                                                className="relative z-50 flex flex-col items-center sm:items-end py-0 sm:py-2 3xl:py-3 text-white dark:text-gray-200 w-full -mt-10 mb-2"
                                            >
                                                <div className="flex flex-col justify-between items-start w-full px-4 overflow-hidden">
                                                    <h2 className="text-lg font-semibold whitespace-nowrap">{game.name}</h2>
                                                    <span className="truncate text-ellipsis text-very-light-gray dark:text-light-gray text-base max-w-full">{game.catch_phrase[locale]}</span>
                                                </div>
                                            </div>
                                            {game.new === true && game.last_chance === null && (
                                                <GameBadge className="game-badge z-10 absolute bg-primary py-1 px-2 sm:px-3 uppercase font-space" style={{ top: '10px' }}>
                                                    {__('book.common.new')}
                                                </GameBadge>
                                            )}
                                            {game.last_chance !== null && (
                                                <GameBadge className="game-badge z-10 absolute bg-warm-gradient py-1 px-2 sm:px-3 uppercase font-space" style={{ top: '10px' }}>
                                                    {__('book.common.last_chance')}
                                                </GameBadge>
                                            )}
                                        </div>
                                    </GameSlide>
                                ))}
                            </CategoriesSwiper>
                            <CatNavigationElement
                                className="absolute left-2 md:left-8 top-[50%] z-50"
                                ref={(el) => (catPrevRef.current[category.id] = el)}
                                onClick={() => {
                                    if (catSwipers.current[category.id]) catSwipers.current[category.id].slidePrev();
                                }}
                            >
                                <svg className="size-5 rotate-90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                                </svg>
                            </CatNavigationElement>
                            <CatNavigationElement
                                className="absolute right-2 md:right-8 top-[50%] z-50"
                                ref={(el) => (catNextRef.current[category.id] = el)}
                                onClick={() => {
                                    if (catSwipers.current[category.id]) catSwipers.current[category.id].slideNext();
                                }}
                            >
                                <svg className="size-5 -rotate-90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                                </svg>
                            </CatNavigationElement>
                        </div>
                    ))}
                    {/*<GamesGrid className="games-grid px-0 pt-0 relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 grid-bordered">*/}
                    {/*    {games.filter((g) => g.popular === false).map((game, index) => (*/}
                    {/*        <GamesGridItem*/}
                    {/*            key={game.id}*/}
                    {/*            className="flex px-2 sm:px-5 py-4 sm:py-6 2xl:px-5 2xl:py-6 relative overflow-hidden rounded-sm"*/}
                    {/*        >*/}
                    {/*            <div className="flex flex-col relative cursor-pointer w-full">*/}
                    {/*                <div className="relative z-10">*/}
                    {/*                    <div className="w-full image overflow-hidden relative rounded-sm">*/}
                    {/*                        <GamePoster*/}
                    {/*                            className="blurred"*/}
                    {/*                            src={game.main_poster}*/}
                    {/*                            srcSet={game.mainPosterSrcset}*/}
                    {/*                            alt={game.name}*/}
                    {/*                            sizes="1px"*/}
                    {/*                            onLoad={(e) => {*/}
                    {/*                                if (e.target.dataset.sized === undefined) {*/}
                    {/*                                    e.target.sizes = Math.ceil(((e.target.getBoundingClientRect().width / window.innerWidth) * 100) * 1.5) + 'vw';*/}
                    {/*                                    e.target.dataset.sized = '';*/}
                    {/*                                    e.target.classList.remove('blurred');*/}
                    {/*                                }*/}
                    {/*                            }}*/}
                    {/*                            loading="lazy"*/}
                    {/*                        />*/}
                    {/*                    </div>*/}
                    {/*                    {game?.is_event && (*/}
                    {/*                        <div className="flex flex-col items-center font-onely-sans absolute top-16 sm:top-24 z-20 w-full text-center text-white" onClick={() => onSelectGame(game)}>*/}
                    {/*                            <span className="text-2xl xl:text-3xl">Jeudi 1er Mai 2O25</span>*/}
                    {/*                            <span className="text-3xl xl:text-4xl">21h3O - OOh</span>*/}
                    {/*                        </div>*/}
                    {/*                    )}*/}
                    {/*                    <GameLogo onClick={() => onSelectGame(game)}>*/}
                    {/*                        <img*/}
                    {/*                            src={game.logo}*/}
                    {/*                            srcSet={game.logoSrcset}*/}
                    {/*                            alt={game.name}*/}
                    {/*                            className="logo"*/}
                    {/*                            style={{whiteSpace: 'nowrap'}}*/}
                    {/*                            sizes="33vw"*/}
                    {/*                            onLoad={(e) => {*/}
                    {/*                                if (e.target.dataset.sized === undefined) {*/}
                    {/*                                    e.target.sizes = Math.ceil((e.target.getBoundingClientRect().width / window.innerWidth) * 100) + 'vw';*/}
                    {/*                                    e.target.dataset.sized = ''*/}
                    {/*                                }*/}
                    {/*                            }}*/}
                    {/*                        />*/}
                    {/*                    </GameLogo>*/}
                    {/*                    <div*/}
                    {/*                        onClick={() => onSelectGame(game)}*/}
                    {/*                        style={{*/}
                    {/*                            position: 'absolute',*/}
                    {/*                            width: '100%',*/}
                    {/*                            height: '100%',*/}
                    {/*                            top: '0',*/}
                    {/*                            left: '0',*/}
                    {/*                            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 42%, #090909 100%)',*/}
                    {/*                        }}*/}
                    {/*                    />*/}
                    {/*                    <div className="absolute top-0 right-0 p-3 md:p-3 3xl:p-4 flex gap-4 z-10">*/}
                    {/*                        <InfosIcon onClick={() => openGameModal(game)}/>*/}
                    {/*                    </div>*/}
                    {/*                    {game.new === true && (*/}
                    {/*                        <GameBadge className="game-badge z-10 absolute bg-primary py-1 px-2 sm:px-3 uppercase font-space">*/}
                    {/*                            {__('book.common.new')}*/}
                    {/*                        </GameBadge>*/}
                    {/*                    )}*/}
                    {/*                    {game.new === false && game.popular === true && (*/}
                    {/*                        <GameBadge className="absolute z-10 py-1 px-2 bg-c-orange sm:px-3 uppercase font-space">*/}
                    {/*                            {__('book.common.popular')}*/}
                    {/*                        </GameBadge>*/}
                    {/*                    )}*/}
                    {/*                    <GameLabel className="game-label">{game.genre[locale]}</GameLabel>*/}
                    {/*                </div>*/}
                    {/*                <div*/}
                    {/*                    onClick={() => onSelectGame(game)}*/}
                    {/*                    className="relative z-50 flex flex-col items-center sm:items-end px-1.5 xl:px-2 sm:px-0 py-0 sm:py-2 3xl:py-3 text-gray-200 w-full h-full -mt-10 sm:-mb-2"*/}
                    {/*                >*/}
                    {/*                    <div className="flex flex-col justify-between items-start w-full px-2 sm:px-3 mb-4 overflow-hidden">*/}
                    {/*                        <h2 className="text-lg font-semibold whitespace-nowrap">{game.name}</h2>*/}
                    {/*                        <span className="truncate text-ellipsis text-light-gray text-sm max-w-full">{game.catch_phrase[locale]}</span>*/}
                    {/*                    </div>*/}
                    {/*                    <div className="flex flex-row items-center sm:items-end w-full">*/}
                    {/*                        {!game?.is_event && (*/}
                    {/*                            <div className="flex flex-row items-center px-2 sm:px-3 gap-1 sm:gap-2 border-r border-light-gray">*/}
                    {/*                                <svg className="size-5 sm:size-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">*/}
                    {/*                                    <rect width="256" height="256" fill="none"/>*/}
                    {/*                                    <circle cx="84" cy="108" r="52" fill="#333" fillOpacity="0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="12"/>*/}
                    {/*                                    <path d="M10.23,200a88,88,0,0,1,147.54,0" fill="#333" fillOpacity="0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="12"/>*/}
                    {/*                                    <path d="M172,160a87.93,87.93,0,0,1,73.77,40" fill="#333" fillOpacity="0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="12"/>*/}
                    {/*                                    <path d="M152.69,59.7A52,52,0,1,1,172,160" fill="#333" fillOpacity="0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="12"/>*/}
                    {/*                                </svg>*/}
                    {/*                                <div className="flex flex-row gap-0.5 text-base"><span>{game.min_players}</span><span>-</span><span>{game.max_players}</span></div>*/}
                    {/*                            </div>*/}
                    {/*                        )}*/}
                    {/*                        <div className="flex flex-row items-center px-2 sm:px-3 gap-1 sm:gap-2">*/}
                    {/*                            <svg className="size-4 sm:size-5 3xl:size-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">*/}
                    {/*                                <rect width="256" height="256" fill="none"/>*/}
                    {/*                                <circle cx="128" cy="128" r="96" fill="#333" fillOpacity="0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>*/}
                    {/*                                <polyline points="128 72 128 128 184 128" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>*/}
                    {/*                            </svg>*/}
                    {/*                            <span className="text-base translate-y-0.25">*/}
                    {/*                            {game?.is_event && '21h30-00h'}*/}
                    {/*                                {!game?.is_event && formatMinutesToHumanReadable(game?.duration ?? settings.default_session_duration)}*/}
                    {/*                        </span>*/}
                    {/*                        </div>*/}
                    {/*                        <div className="flex flex-row flex-grow text-right items-center justify-end px-2 sm:px-3 gap-1 sm:gap-2">*/}
                    {/*                    <span className="text-base">*/}
                    {/*                        {__('book.common.from')} <strong className="text-white font-semibold">{numbro(game.base_price / 100).formatCurrency({mantissa: 2})}</strong> / pp*/}
                    {/*                    </span>*/}
                    {/*                        </div>*/}
                    {/*                    </div>*/}
                    {/*                </div>*/}
                    {/*            </div>*/}
                    {/*        </GamesGridItem>*/}
                    {/*    ))}*/}
                    {/*</GamesGrid>*/}
                </>
            )}
            {!loading && (
                <Transition
                    as={Fragment}
                    show={isScrolled}
                    enter="ease-out transition transform duration-700"
                    enterFrom="opacity-0 -translate-y-4"
                    enterTo="opacity-100 translate-y-0"
                    leave="ease-in transition transform duration-200"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-4"
                >
                    <div
                        id="games-to-top"
                        className="flex lg:hidden fixed z-[120] rounded-full p-3.5 sm:p-4 cursor-pointer border transition text-gray-900"
                        style={{background: 'rgba(255, 255, 255, 0.75)', borderColor: 'rgba(255, 255, 255, 0.9)'}}
                        onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
                    >
                        <svg className="size-5 sm:size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                            <rect width="256" height="256" fill="none"/>
                            <polyline points="48 200 128 120 208 200" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                            <polyline points="48 120 128 40 208 120" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                        </svg>
                    </div>
                </Transition>
            )}
            {loading && (
                <div className="absolute flex items-center justify-center w-full flex-grow h-full">
                    <div className="size-10 sm:size-16 rounded-full animate-spin border-y-2 border-solid border-white border-t-transparent"></div>
                </div>
            )}
            <GameDetailsModal game={currentGame} locale={locale} onClose={closeGameModal} />
        </section>
    );
}
